<template>
  <div class="calendar">
    <div class="calendar__main">
      <div class="calendar__actions">
        <div class="calendar-nav calendar__nav">
          <div
            @click="goToPreviousMonth()"
            class="calendar__action calendar__button-left"
          />
          <div
            v-if="!isLastMonth"
            @click="goToNextMonth()"
            class="calendar__action calendar__button-right"
          />
          <VueDatePicker
            ref="datepicker"
            month-picker
            auto-apply
            :locale="esLocale"
            :model-value="monthPickerValue"
            :formats="{ input: calendarMonthFormatter }"
            :max-date="calendarLastDate.toDate()"
            :ui="{ input: 'calendar__action calendar__month-picker' }"
            @update:model-value="onMonthPicked"
          />
        </div>
      </div>
      <div class="calendar__content">
        <div class="calendar-header calendar__header">
          <div class="calendar-header__weekdays">
            <div
              class="calendar-header__weekday"
              v-for="day in weekDays"
              :key="day"
            >
              {{ day }}
            </div>
          </div>
        </div>
        <div class="calendar__body">
          <div
            class="calendar__row"
            v-for="week in totalWeeks"
            :key="week"
          >
            <div
              class="calendar__cell"
              v-for="(day, index) in weekDays"
              :key="day"
            >
              <calendar-cell
                :is-empty="emptyDay(week, index)"
                :date="calculateDateFromPosition(week, index)"
                :reservations="getReservationsForDate(week, index)"
                :inactive-reservations="getInactiveReservationsForDate(week, index)"
                :month="currentMonth"
                :year="currentYear"
                :holiday-name="holidayName(currentYear, currentMonth, calculateDateFromPosition(week, index))"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <transition name="modal">
      <modal
        v-show="showModal"
      />
    </transition>
  </div>
</template>

<script>

import * as humps from 'humps';
import moment from 'moment-timezone';
import 'moment/dist/locale/es';
import throttle from 'lodash.throttle';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { es as esLocale } from 'date-fns/locale';
import '@vuepic/vue-datepicker/dist/main.css';

import CalendarCell from './calendar-cell.vue';
import Modal from './modal.vue';
import * as actions from '../store/action-types';

moment.locale('es-CL');
moment.tz.setDefault('America/Santiago');
if (moment.locale() !== 'es') {
  console.warn('[calendar] Spanish moment locale failed to register - check for duplicate moment copies.');
}
const DAYS_IN_WEEK = 7;
// The grid is Monday-first. ISO weekdays (1 = Monday ... 7 = Sunday) are
// locale-independent, so the headers and the column offset below can never
// disagree the way locale-derived weekday() numbers can.
const MONDAY_ISO = 1;
const WEEKS_IN_A_YEAR = 53;

export default {
  name: 'Calendar',
  components: {
    CalendarCell,
    Modal,
    VueDatePicker,
  },
  props: {
    arenaTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      esLocale,
      weekDays: [...Array(DAYS_IN_WEEK).keys()]
        .map(offset => moment().isoWeekday(MONDAY_ISO + offset).format('dddd')),
    };
  },
  created() {
    this.fetchReservations();
    this.subscribeToCalendarEvents();
  },
  channels: {
    ReservationsChannel: {
      connected() {
        console.log('connected');
      },
      received(event) {
        this.onReservationEventReceived(event);
      },
      disconnected() {
        console.log('disconnected');
      },
    },
  },
  computed: {
    firstDay() {
      return moment(this.$store.state.calendar.calendarDate);
    },
    selectedReservation() {
      return this.$store.getters.selectedReservation();
    },
    currentReservations() {
      return this.$store.getters.reservations();
    },
    currentFormattedMonth() {
      return this.firstDay.format('MMMM');
    },
    currentMonth() {
      return this.firstDay.month();
    },
    daysInMonth() {
      return this.firstDay.daysInMonth();
    },
    totalWeeks() {
      const totalDays = this.daysInMonth + this.firstWeekday;

      return Math.ceil(totalDays / DAYS_IN_WEEK);
    },
    firstWeekday() {
      return this.firstDay.isoWeekday() - MONDAY_ISO;
    },
    groupedReservations() {
      return this.$store.getters.reservationsGroupedByDay;
    },
    groupedInactiveReservations() {
      return this.$store.getters.inactiveReservationsGroupedByDay;
    },
    currentYear() {
      return this.firstDay.format('YYYY');
    },
    showModal() {
      return this.$store.state.calendar.showModal;
    },
    calendarLastDate() {
      return moment(import.meta.env.VITE_CALENDAR_LAST_DATE, 'DD/MM/YYYY');
    },
    monthPickerValue() {
      return { month: this.firstDay.month(), year: this.firstDay.year() };
    },
    // Also true past the limit: a reservation link jumps straight to its own
    // month, which can be later than the last date if the limit was lowered.
    isLastMonth() {
      return !this.firstDay.isBefore(this.calendarLastDate, 'month');
    },
  },
  methods: {
    calendarMonthFormatter(date) {
      const value = date instanceof Date ? date : new Date(date.year, date.month, 1);

      return moment(value).format('MMMM YYYY');
    },
    onMonthPicked(value) {
      if (!value) return;
      this.onMonthSelected(new Date(value.year, value.month, 1));
    },
    onMonthSelected(date) {
      this.$store.dispatch(actions.CHANGE_MONTH, { date });
    },
    onReservationEventReceived: throttle(function (event) {
      this.$store.dispatch(
        actions.RESERVATION_EVENT_RECEIVED,
        humps.camelizeKeys(event),
      );
    }, 500, { leading: true, trailing: true }), // eslint-disable-line no-magic-numbers
    subscribeToCalendarEvents() {
      this.$cable.subscribe({ channel: 'ReservationsChannel' });
    },
    goToPreviousMonth() {
      this.$store.dispatch(actions.CHANGE_MONTH, {
        date: moment(this.firstDay.subtract(1, 'months')).toDate(),
      });
    },
    goToNextMonth() {
      this.$store.dispatch(actions.CHANGE_MONTH, {
        date: moment(this.firstDay.add(1, 'months')).toDate(),
      });
    },
    fetchReservations() {
      this.$store.dispatch(actions.CHANGE_MONTH, { date: this.firstDay.toDate() });
    },
    calculateDateFromPosition(week, index) {
      return (week - 1) * DAYS_IN_WEEK + (index + 1) - this.firstWeekday;
    },
    emptyDay(week, index) {
      return (week === 1 && this.firstWeekday > index) ||
      this.calculateDateFromPosition(week, index) > this.daysInMonth;
    },
    getReservationsForDate(week, index) {
      const dateReservations = this.groupedReservations[this.calculateDateFromPosition(week, index)];

      return dateReservations;
    },
    getInactiveReservationsForDate(week, index) {
      const dateInactiveReservations = this.groupedInactiveReservations[this.calculateDateFromPosition(week, index)];

      return dateInactiveReservations;
    },
    dateDayInCurrentMonth(date, currentDate) {
      if (moment(date).month() < moment(currentDate).month()) {
        return 1;
      } else if (moment(date).month() > moment(currentDate).month()) {
        return moment(currentDate).endOf('month').date();
      }

      return moment(date).date();
    },
    holidayName(year, month, day) {
      let name = '';
      this.$store.state.calendar.holidays.forEach(holiday => {
        const cellDate = moment(new Date(year, month, day)).format('YYYY-MM-DD');
        if (holiday.date === cellDate) {
          name = holiday.name;
        }
      });

      return name;
    },
  },
};
</script>
